import axios from 'axios';
import { auth } from './firebaseConfig.ts';
import { onAuthStateChanged } from 'firebase/auth';
import * as Sentry from '@sentry/react';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'en',
    },
});

let currentUserPromise: Promise<any> | null = null;

const getCurrentUser = () => {
    if (!currentUserPromise) {
        currentUserPromise = new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                unsubscribe();
                resolve(user);
            });
        });
    }
    return currentUserPromise;
};

const signOut = () => {
    currentUserPromise = null;
    localStorage.removeItem('auth');
    localStorage.removeItem('ra_token');
    localStorage.removeItem('ra_uid');
    localStorage.removeItem('ra_role');
    localStorage.removeItem('ra_shabbat_only');
    window.location.href = '/login';
};

axiosInstance.interceptors.request.use(
    async (config) => {
        if (config.url?.includes('/auth/sign-in')) {
            return config;
        }

        const user = await getCurrentUser();

        if (user) {
            // Not getIdToken(true): Firebase returns a cached token and refreshes
            // it on its own near expiry. Forcing a refresh made a network call to
            // Google on every single request, so any blip on a restaurant's wifi
            // became a failed request.
            const token = await user.getIdToken();
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        // One 401 is not proof the session is over — a token can expire mid-flight.
        // Force a refresh and replay the request once before giving up. Previously
        // an unconditional redirect sat above this block, so it never ran and a
        // single 401 dumped the user back to the login screen with no explanation.
        if (!originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const user = auth.currentUser ?? (await getCurrentUser());

                if (user) {
                    const newToken = await user.getIdToken(true);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return await axiosInstance(originalRequest);
                }
            } catch (refreshError) {
                Sentry.captureException(refreshError);
            }
        }

        // Refresh failed, or the retry was rejected too: the session really is over.
        signOut();
        return Promise.reject(error);
    }
);

auth.onAuthStateChanged((user) => {
    if (!user) {
        currentUserPromise = null;
    }
});

export default axiosInstance;
